import axios from 'axios';

/**
 * True only for an HTTP 403 from the API.
 *
 * ── WHY THIS IS A HELPER AND NOT AN INTERCEPTOR ─────────────────────────────
 * The audit's F-2 finding is that a 403 on a list query is rendered as
 * "No users yet — Add your first user to get started." — an authorization
 * failure presented as a fact about the business, which is the D-06 anti-pattern
 * `QueryErrorState` already exists to prevent.
 *
 * The fix is deliberately per-call rather than global. `apiClient`'s response
 * interceptor already owns 401 (clear session) and the subscription/tenant 403
 * codes (route to the access-restricted screen); widening it to catch every 403
 * would change the behaviour of every existing caller at once, including ones
 * where a 403 is already handled sensibly. A page asks this question about its
 * own query, and nothing else changes.
 *
 * Deliberately narrow:
 *  - 401 is NOT forbidden — session expiry is the interceptor's job and must
 *    keep working exactly as it does.
 *  - A network error, a timeout or a 500 is NOT forbidden — those are genuine
 *    failures and belong in `QueryErrorState`, which offers a retry.
 *  - A 200 carrying an empty array is NOT forbidden — that is a real empty
 *    dataset and must keep its `EmptyState`.
 */
export function isForbiddenError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}
